<template>
  <form class="event-form" @submit.prevent="$emit('submit')">
    <h4>{{ editingId ? 'Edit Event' : 'Create New Event' }}</h4>

    <div class="form-grid">
      <div class="form-group full-width">
        <label for="title">Title *</label>
        <input
          id="title"
          :value="form.title"
          type="text"
          required
          placeholder="e.g. Home Inspection"
          @input="$emit('update:form', { ...form, title: $event.target.value })"
        />
      </div>

      <div class="form-group checkbox-group full-width">
        <label for="isAllDay">
          <input
            id="isAllDay"
            :checked="form.isAllDay"
            type="checkbox"
            @change="$emit('update:form', { ...form, isAllDay: $event.target.checked })"
          />
          All-day event
        </label>
      </div>

      <div class="form-group">
        <label for="startTime">{{ form.isAllDay ? 'Date *' : 'Start Date & Time *' }}</label>
        <input
          id="startTime"
          :value="form.startTime"
          :type="form.isAllDay ? 'date' : 'datetime-local'"
          required
          @input="$emit('update:form', { ...form, startTime: $event.target.value })"
        />
      </div>

      <div class="form-group full-width">
        <label for="description">Description</label>
        <textarea
          id="description"
          :value="form.description"
          rows="2"
          placeholder="Add notes, location, or details..."
          @input="$emit('update:form', { ...form, description: $event.target.value })"
        />
      </div>
    </div>

    <div class="form-actions">
      <button type="submit" class="btn-submit" :disabled="loading">
        {{ loading ? 'Saving...' : (editingId ? 'Update Event' : 'Save Event') }}
      </button>
      <button v-if="editingId" type="button" class="btn-cancel" @click="$emit('cancel')">
        Cancel Edit
      </button>
    </div>
  </form>
</template>

<script setup>
defineProps({
  form: {
    type: Object,
    required: true
  },
  editingId: {
    type: String,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
});

defineEmits(['submit', 'cancel', 'update:form']);
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "@/assets/styles/abstract/variables" as *;

.event-form {
  background: color.mix($white, #ffffff, 40%);
  border: 1px solid color.mix($light-brown, #ffffff, 50%);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;

  h4 {
    margin: 0 0 1rem 0;
    font-size: 1rem;
    color: $secondary-color;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;

    &.full-width {
      grid-column: 1 / -1;
    }

    &.checkbox-group {
      flex-direction: row;
      align-items: center;

      label {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.85rem;
        font-weight: 500;
        color: $secondary-color;
        cursor: pointer;

        input[type="checkbox"] {
          cursor: pointer;
          width: 16px;
          height: 16px;
          accent-color: $dark-blue;
        }
      }
    }

    label {
      font-size: 0.8rem;
      font-weight: 600;
      color: $dark-pink;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    input, textarea {
      padding: 0.6rem 0.75rem;
      border: 1px solid color.mix($light-brown, $secondary-color, 70%);
      border-radius: 8px;
      font-size: 0.9rem;
      outline: none;
      background: #ffffff;
      color: $secondary-color;
      transition: border-color 0.2s, box-shadow 0.2s;

      &:focus {
        border-color: $mid-blue;
        box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.15);
      }
    }
  }

  .form-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.25rem;

    .btn-submit {
      flex: 1;
      background: $dark-blue;
      color: $white;
      border: none;
      border-radius: 8px;
      padding: 0.65rem;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s ease;

      &:hover {
        background: $mid-blue;
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    .btn-cancel {
      background: transparent;
      border: 1px solid color.mix($light-brown, $secondary-color, 70%);
      color: $grey-back;
      border-radius: 8px;
      padding: 0.65rem 1rem;
      font-weight: 500;
      cursor: pointer;

      &:hover {
        background: $white;
        color: $secondary-color;
      }
    }
  }
}
</style>